import React, { useState, useEffect } from "react";
import { Table, Label, Icon, Button, Confirm, Input, Loader, Segment, Statistic, Header, Divider, Grid, Image } from "semantic-ui-react";
import { RegisteredPayment } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { BasicModal } from "../../../Shared";
import { buildStorageUrl } from "../../../../utils";
import Swal from "sweetalert2";
import "./StudentDebts.scss";

const registeredPaymentController = new RegisteredPayment();

export function StudentDebts(props) {
  const { studentActive, academicCycleId } = props;
  const { accessToken } = useAuth();
  const [debts, setDebts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);

  const [showConfirmWaive, setShowConfirmWaive] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [waiveNotes, setWaiveNotes] = useState("");
  const [showVoucherModal, setShowVoucherModal] = useState(false);

  const onReload = () => setReload((prev) => !prev);

  const handleOpenVoucher = (debt) => {
    setSelectedDebt(debt);
    setShowVoucherModal(true);
  };

  useEffect(() => {
    if (!studentActive?.id) return;
    (async () => {
      try {
        setLoading(true);
        const response = await registeredPaymentController.getStudentDebts(
          studentActive.id,
          accessToken,
          null,
          academicCycleId
        );
        setDebts(response?.debts || []);
        setSummary(response?.summary || null);
      } catch (error) {
        console.error("Error al cargar deudas del estudiante", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [studentActive?.id, reload, accessToken, academicCycleId]);

  const handleOpenWaive = (debt) => {
    setSelectedDebt(debt);
    setWaiveNotes("");
    setShowConfirmWaive(true);
  };

  const handleWaive = async () => {
    try {
      if (!selectedDebt) return;
      await registeredPaymentController.waiveDebt(accessToken, selectedDebt.id, waiveNotes);
      setShowConfirmWaive(false);
      Swal.fire({
        icon: "success",
        title: "Deuda condonada",
        text: "La deuda ha sido condonada de manera exitosa.",
        background: "#1f2937",
        color: "#fff",
        confirmButtonColor: "#3b82f6",
      });
      onReload();
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo condonar la deuda.",
        background: "#1f2937",
        color: "#fff",
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PAID":
        return "green";
      case "PARTIAL":
        return "yellow";
      case "PENDING":
        return "blue";
      case "OVERDUE":
        return "red";
      case "WAIVED":
        return "grey";
      default:
        return "grey";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "PAID":
        return "PAGADO";
      case "PARTIAL":
        return "PAGO PARCIAL";
      case "PENDING":
        return "PENDIENTE";
      case "OVERDUE":
        return "VENCIDO";
      case "WAIVED":
        return "CONDONADO";
      default:
        return status;
    }
  };

  const getMonthName = (monthNumber) => {
    if (!monthNumber) return "";
    const months = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];
    return months[monthNumber - 1] || "";
  };

  if (loading) {
    return <Loader active inline="centered" content="Cargando cuentas por cobrar (CXC)..." />;
  }

  return (
    <div className="student-debts">
      {summary && (
        <Segment className="student-debts__summary-box">
          <Statistic.Group size="tiny" widths="four">
            <Statistic>
              <Statistic.Value>S/ {Number(summary.totalDue).toFixed(2)}</Statistic.Value>
              <Statistic.Label>Deuda Total</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value className="text-success">S/ {Number(summary.totalPaid).toFixed(2)}</Statistic.Value>
              <Statistic.Label>Total Pagado</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value className="text-warning">S/ {Number(summary.totalPending).toFixed(2)}</Statistic.Value>
              <Statistic.Label>Saldo Pendiente</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value className={summary.overdue > 0 ? "text-danger" : ""}>
                {summary.overdue}
              </Statistic.Value>
              <Statistic.Label>Conceptos Vencidos</Statistic.Label>
            </Statistic>
          </Statistic.Group>
        </Segment>
      )}

      {debts.length === 0 ? (
        <Segment placeholder textAlign="center" className="student-debts__empty">
          <Header icon>
            <Icon name="check circle outline" color="green" />
            El estudiante no registra deudas ni cuentas por cobrar.
          </Header>
        </Segment>
      ) : (
        <div className="student-debts__table-wrapper">
          <Table celled className="student-debts__table">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Concepto</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Fecha Vencimiento</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Monto Total</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Monto Pagado</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Saldo Pendiente</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Mora</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Estado</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Acciones</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {debts.map((debt) => (
                <Table.Row key={debt.id} warning={debt.status === "PARTIAL"} negative={debt.status === "OVERDUE"}>
                  <Table.Cell className="font-bold">{debt.concept?.name}</Table.Cell>
                  <Table.Cell textAlign="center">
                    {debt.due_date ? new Date(debt.due_date).toLocaleDateString() : "N/A"}
                  </Table.Cell>
                  <Table.Cell textAlign="right" className="font-bold">
                    S/ {Number(debt.amount_due).toFixed(2)}
                  </Table.Cell>
                  <Table.Cell textAlign="right" className="text-success">
                    S/ {Number(debt.amount_paid).toFixed(2)}
                  </Table.Cell>
                  <Table.Cell textAlign="right" className="text-warning font-bold">
                    S/ {Number(debt.amount_pending).toFixed(2)}
                  </Table.Cell>
                  <Table.Cell textAlign="right" className={debt.isLateFeeWaived ? "text-success font-bold" : "text-danger"}>
                    {debt.isLateFeeWaived ? "EXONERADA" : `S/ ${Number(debt.late_fees_accumulated).toFixed(2)}`}
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Label color={getStatusColor(debt.status)} size="small">
                      {getStatusText(debt.status)}
                    </Label>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {debt.path ? (
                      <Button
                        size="mini"
                        color="blue"
                        icon="eye"
                        title="Ver Comprobante"
                        onClick={() => handleOpenVoucher(debt)}
                      />
                    ) : (
                      <span style={{ fontStyle: "italic", color: "grey", fontSize: "0.85rem" }}>Sin comprobante</span>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      <Confirm
        open={showConfirmWaive}
        onCancel={() => setShowConfirmWaive(false)}
        onConfirm={handleWaive}
        title="Confirmar Condonación de Deuda"
        confirmButton="Condonar"
        cancelButton="Cancelar"
        content={
          <div className="waive-confirm-content" style={{ padding: "1.5rem" }}>
            <p>¿Está seguro de que desea condonar la deuda del concepto <strong>{selectedDebt?.concept?.name}</strong>?</p>
            <p>Esta acción es irreversible y marcará el estado de la deuda como <strong>CONDONADO</strong>.</p>
            <div style={{ marginTop: "1rem" }}>
              <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>Motivo de la condonación (Notas):</label>
              <Input
                fluid
                placeholder="Ingrese el motivo de la condonación..."
                value={waiveNotes}
                onChange={(e) => setWaiveNotes(e.target.value)}
              />
            </div>
          </div>
        }
      />

      <BasicModal
        title={`Comprobante de ${selectedDebt?.concept?.name}`}
        show={showVoucherModal}
        close={() => setShowVoucherModal(false)}
        size="small"
      >
        <div style={{ textAlign: "center", padding: "1rem" }}>
          {selectedDebt?.path ? (
            <Image
              src={buildStorageUrl(selectedDebt.path)}
              rounded
              fluid
              centered
              style={{ maxHeight: "70vh", objectFit: "contain" }}
            />
          ) : (
            <p>No hay comprobante adjunto para esta deuda.</p>
          )}
          <Divider />
          <Grid columns={2}>
            <Grid.Column>
              <strong>Código Transacción:</strong>{" "}
              {selectedDebt?.transactionCode || "N/A"}
            </Grid.Column>
            <Grid.Column>
              <strong>Monto Pagado:</strong> S/{" "}
              {Number(selectedDebt?.amount_paid || 0).toFixed(2)}
            </Grid.Column>
          </Grid>
        </div>
      </BasicModal>
    </div>
  );
}
